import React, { useState, useEffect } from 'react';
import { Save, Database, AlertTriangle, CheckCircle, Lock, Copy, Info, Upload, Download, Trash2, FileText, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from './Modal';
import { v4 as uuidv4 } from 'uuid';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { listCsvBackupsServer, deleteCsvBackupServer, parseCsv, type ServerCsvBackup } from '../lib/csvBackups';

interface Connection {
  id: string;
  connectionName: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  isActive: boolean;
}

export function ConnectionPage() {
  const [formData, setFormData] = useState({
    connectionName: '',
    supabaseUrl: '',
    supabaseAnonKey: '',
  });
  const [savedData, setSavedData] = useState({
    connectionName: '',
    supabaseUrl: '',
    supabaseAnonKey: '',
  });
  const [wooUrl, setWooUrl] = useState('');
  const [wooConsumerKey, setWooConsumerKey] = useState('');
  const [wooConsumerSecret, setWooConsumerSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [wooTestStatus, setWooTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [wooTestMessage, setWooTestMessage] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showSqlInfo, setShowSqlInfo] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportData, setExportData] = useState('');
  const [importData, setImportData] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [showConnectionSelector, setShowConnectionSelector] = useState(false);

  // Backups CSV des paniers (dossier /csv/ en ligne)
  const [csvBackups, setCsvBackups] = useState<ServerCsvBackup[]>([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [viewingCsv, setViewingCsv] = useState<ServerCsvBackup | null>(null);
  const [viewingCsvContent, setViewingCsvContent] = useState('');
  const [viewingCsvLoading, setViewingCsvLoading] = useState(false);
  const [csvSearch, setCsvSearch] = useState('');

  // Secret encryption key (hardcoded for simplicity, but can be improved)
  const ENCRYPTION_KEY = 'LotisModeSecureKey2025';

  useEffect(() => {
    // Charger les configurations précédemment sauvegardées
    loadSavedConnections();
    refreshCsvBackups();
  }, []);

  const refreshCsvBackups = async () => {
    setCsvLoading(true);
    setCsvError('');
    try {
      const list = await listCsvBackupsServer();
      setCsvBackups(list);
    } catch (e: any) {
      console.error('Erreur chargement backups CSV:', e);
      setCsvError('Impossible de charger les backups CSV en ligne (serveur ou dossier /csv indisponible).');
      setCsvBackups([]);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleDeleteCsvBackup = async (filename: string) => {
    try {
      await deleteCsvBackupServer(filename);
      toast.success('Backup CSV supprimé');
      await refreshCsvBackups();
    } catch (e: any) {
      toast.error(e.message || 'Erreur lors de la suppression');
    }
  };

  const handleClearCsvBackups = async () => {
    try {
      for (const b of csvBackups) {
        await deleteCsvBackupServer(b.name);
      }
      toast.success('Tous les backups CSV ont été supprimés');
      await refreshCsvBackups();
    } catch (e: any) {
      toast.error(e.message || 'Erreur lors de la suppression');
      await refreshCsvBackups();
    }
  };

  const handleViewCsvBackup = async (backup: ServerCsvBackup) => {
    setViewingCsv(backup);
    setViewingCsvContent('');
    setCsvSearch('');
    setViewingCsvLoading(true);
    try {
      const res = await axios.get(backup.url, { responseType: 'text', timeout: 20000 });
      setViewingCsvContent(typeof res.data === 'string' ? res.data : String(res.data));
    } catch (e) {
      console.error('Erreur lecture CSV:', e);
      setViewingCsvContent('');
      toast.error('Impossible de charger le contenu du fichier CSV');
    } finally {
      setViewingCsvLoading(false);
    }
  };

  const loadWooValues = () => {
    setWooUrl(localStorage.getItem('woo_api_url') || import.meta.env.VITE_WOO_API_URL || '');
    setWooConsumerKey(localStorage.getItem('woo_consumer_key') || import.meta.env.VITE_WOO_CONSUMER_KEY || '');
    setWooConsumerSecret(localStorage.getItem('woo_consumer_secret') || import.meta.env.VITE_WOO_CONSUMER_SECRET || '');
  };

  const loadSavedConnections = () => {
    // Charger WooCommerce
    loadWooValues();

    // Charger depuis localStorage
    const savedConnectionsJson = localStorage.getItem('saved_connections');
    let loadedConnections: Connection[] = [];
    
    if (savedConnectionsJson) {
      try {
        loadedConnections = JSON.parse(savedConnectionsJson);
        setConnections(loadedConnections);
        
        // Trouver la connexion active
        const activeConnection = loadedConnections.find(c => c.isActive);
        
        if (activeConnection) {
          setFormData({
            connectionName: activeConnection.connectionName,
            supabaseUrl: activeConnection.supabaseUrl,
            supabaseAnonKey: activeConnection.supabaseAnonKey,
          });
          setSavedData({
            connectionName: activeConnection.connectionName,
            supabaseUrl: activeConnection.supabaseUrl,
            supabaseAnonKey: activeConnection.supabaseAnonKey,
          });
        } else {
          loadDefaultSupabaseValues();
        }
      } catch (error) {
        console.error('Erreur lors du chargement des connexions sauvegardées:', error);
        // Fallback aux valeurs par défaut
        loadDefaultSupabaseValues();
      }
    } else {
      // Aucune connexion sauvegardée, charger les valeurs par défaut
      loadDefaultSupabaseValues();
    }
  };

  const loadDefaultSupabaseValues = () => {
    const name = localStorage.getItem('connection_name');
    const url = localStorage.getItem('supabase_url');
    const key = localStorage.getItem('supabase_anon_key');
    
    if (url && key) {
      setFormData({
        connectionName: name || '',
        supabaseUrl: url,
        supabaseAnonKey: key,
      });
      setSavedData({
        connectionName: name || '',
        supabaseUrl: url,
        supabaseAnonKey: key,
      });
    } else {
      // Utiliser les valeurs par défaut des variables d'environnement
      setFormData({
        connectionName: '',
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
        supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setPasswordError('');
  };

  const handleAuthenticate = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Recep2025') {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Mot de passe incorrect');
    }
  };

  const testConnection = async () => {
    try {
      setTestStatus('testing');
      setTestMessage('Test de connexion en cours...');

      // Créer un client Supabase temporaire pour tester la connexion
      const { createClient } = await import('@supabase/supabase-js');
      const testClient = createClient(
        formData.supabaseUrl,
        formData.supabaseAnonKey,
        {
          auth: {
            persistSession: false,
            autoRefreshToken: false,
          }
        }
      );

      // Tester la connexion avec une requête simple
      const { error } = await testClient
        .from('lotismode_products')
        .select('id')
        .limit(1);

      if (error) {
        console.error('Erreur de connexion:', error);
        setTestStatus('error');
        setTestMessage(`Erreur de connexion: ${error.message}`);
        return false;
      }

      setTestStatus('success');
      setTestMessage('Connexion établie avec succès!');
      return true;
    } catch (error: any) {
      console.error('Erreur lors du test:', error);
      setTestStatus('error');
      setTestMessage(`Erreur: ${error.message}`);
      return false;
    }
  };

  const testWooConnection = async () => {
    try {
      setWooTestStatus('testing');
      setWooTestMessage('Test de connexion WooCommerce en cours...');

      if (!wooUrl || !wooConsumerKey || !wooConsumerSecret) {
        setWooTestStatus('error');
        setWooTestMessage('Configuration WooCommerce manquante (URL, clé et secret sont requis)');
        return false;
      }

      const response = await fetch(`${wooUrl}/wp-json/wc/v3/products?per_page=1`, {
        method: 'GET',
        headers: {
          'Authorization': 'Basic ' + btoa(`${wooConsumerKey}:${wooConsumerSecret}`),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      setWooTestStatus('success');
      setWooTestMessage(`Connexion WooCommerce réussie (${wooUrl})`);
      return true;
    } catch (error: any) {
      console.error('Erreur lors du test WooCommerce:', error);
      setWooTestStatus('error');
      setWooTestMessage(`Erreur: ${error.message}`);
      return false;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Valider les entrées
      if (!formData.supabaseUrl || !formData.supabaseAnonKey) {
        toast.error('Veuillez saisir l\'URL et la clé d\'API Supabase');
        return;
      }

      // Tester la connexion avant de sauvegarder
      const isConnected = await testConnection();
      
      if (!isConnected) {
        toast.error('Impossible de se connecter avec ces paramètres');
        return;
      }

      // Générer un ID unique pour la connexion si c'est une nouvelle
      const connectionId = selectedConnectionId || uuidv4();
      
      // Mettre à jour la liste des connexions
      const updatedConnections = [...connections.filter(c => c.id !== connectionId)];
      
      // Désactiver toutes les connexions existantes
      updatedConnections.forEach(conn => {
        conn.isActive = false;
      });
      
      // Ajouter la nouvelle connexion comme active
      updatedConnections.push({
        id: connectionId,
        connectionName: formData.connectionName || 'Sans nom',
        supabaseUrl: formData.supabaseUrl,
        supabaseAnonKey: formData.supabaseAnonKey,
        isActive: true
      });
      
      // Sauvegarder dans localStorage
      localStorage.setItem('saved_connections', JSON.stringify(updatedConnections));
      localStorage.setItem('woo_api_url', wooUrl);
      localStorage.setItem('woo_consumer_key', wooConsumerKey);
      localStorage.setItem('woo_consumer_secret', wooConsumerSecret);
      
      // Mettre à jour l'état des connexions
      setConnections(updatedConnections);
      
      // Mettre également à jour les variables d'environnement locales pour compatibilité
      localStorage.setItem('connection_name', formData.connectionName);
      localStorage.setItem('supabase_url', formData.supabaseUrl);
      localStorage.setItem('supabase_anon_key', formData.supabaseAnonKey);
      
      // Mettre à jour l'état des données sauvegardées
      setSavedData({
        connectionName: formData.connectionName,
        supabaseUrl: formData.supabaseUrl,
        supabaseAnonKey: formData.supabaseAnonKey,
      });

      toast.success('Configuration sauvegardée avec succès');
      
      // Informer l'utilisateur qu'un rafraîchissement est nécessaire pour appliquer les changements
      toast.success('Rafraîchissement de l\'application...', {
        duration: 2000
      });
      
      // Rafraîchir la page après un court délai pour appliquer les nouvelles configurations
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(`Erreur lors de la sauvegarde: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Réinitialiser aux valeurs des variables d'environnement
    setFormData({
      connectionName: '',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    });
    setWooUrl(import.meta.env.VITE_WOO_API_URL || '');
    setWooConsumerKey(import.meta.env.VITE_WOO_CONSUMER_KEY || '');
    setWooConsumerSecret(import.meta.env.VITE_WOO_CONSUMER_SECRET || '');
    setTestStatus('idle');
    setTestMessage('');
    setWooTestStatus('idle');
    setWooTestMessage('');
  };

  const handleClear = () => {
    // Supprimer les valeurs stockées et réinitialiser le formulaire
    localStorage.removeItem('connection_name');
    localStorage.removeItem('supabase_url');
    localStorage.removeItem('supabase_anon_key');
    localStorage.removeItem('woo_api_url');
    localStorage.removeItem('woo_consumer_key');
    localStorage.removeItem('woo_consumer_secret');
    
    setFormData({
      connectionName: '',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    });
    
    setSavedData({
      connectionName: '',
      supabaseUrl: '',
      supabaseAnonKey: '',
    });

    setWooUrl(import.meta.env.VITE_WOO_API_URL || '');
    setWooConsumerKey(import.meta.env.VITE_WOO_CONSUMER_KEY || '');
    setWooConsumerSecret(import.meta.env.VITE_WOO_CONSUMER_SECRET || '');
    
    setTestStatus('idle');
    setTestMessage('');
    setWooTestStatus('idle');
    setWooTestMessage('');
    
    toast.success('Configuration réinitialisée');
  };

  const copySqlToClipboard = (sql: string) => {
    navigator.clipboard.writeText(sql).then(() => {
      toast.success('SQL copié dans le presse-papier');
    });
  };

  const handleEditConnection = (connection: Connection) => {
    setFormData({
      connectionName: connection.connectionName,
      supabaseUrl: connection.supabaseUrl,
      supabaseAnonKey: connection.supabaseAnonKey,
    });
    setSelectedConnectionId(connection.id);
  };

  const handleActivateConnection = async (connection: Connection) => {
    try {
      // Désactiver toutes les connexions
      const updatedConnections = connections.map(conn => ({
        ...conn,
        isActive: conn.id === connection.id
      }));
      
      // Mettre à jour localStorage
      localStorage.setItem('saved_connections', JSON.stringify(updatedConnections));
      localStorage.setItem('connection_name', connection.connectionName);
      localStorage.setItem('supabase_url', connection.supabaseUrl);
      localStorage.setItem('supabase_anon_key', connection.supabaseAnonKey);
      
      // Mettre à jour l'état
      setConnections(updatedConnections);
      setFormData({
        connectionName: connection.connectionName,
        supabaseUrl: connection.supabaseUrl,
        supabaseAnonKey: connection.supabaseAnonKey,
      });
      setSavedData({
        connectionName: connection.connectionName,
        supabaseUrl: connection.supabaseUrl,
        supabaseAnonKey: connection.supabaseAnonKey,
      });
      
      toast.success(`Connexion "${connection.connectionName}" activée`);
      
      // Rafraîchir l'application pour appliquer les changements
      toast.success('Rafraîchissement de l\'application...', {
        duration: 2000
      });
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('Erreur lors de l\'activation de la connexion:', error);
      toast.error('Erreur lors de l\'activation de la connexion');
    }
  };

  const handleDeleteConnection = (connectionId: string) => {
    setSelectedConnectionId(connectionId);
    setShowDeleteModal(true);
  };

  const confirmDeleteConnection = () => {
    if (!selectedConnectionId) return;
    
    // Filtrer les connexions pour supprimer celle sélectionnée
    const updatedConnections = connections.filter(conn => conn.id !== selectedConnectionId);
    
    // Mettre à jour localStorage
    localStorage.setItem('saved_connections', JSON.stringify(updatedConnections));
    
    // Mettre à jour l'état
    setConnections(updatedConnections);
    setSelectedConnectionId(null);
    setShowDeleteModal(false);
    
    // Si la connexion supprimée était active, utiliser les valeurs par défaut
    const wasActive = connections.find(conn => conn.id === selectedConnectionId)?.isActive;
    if (wasActive) {
      localStorage.removeItem('connection_name');
      localStorage.removeItem('supabase_url');
      localStorage.removeItem('supabase_anon_key');
      
      toast.success('Connexion active supprimée, retour aux paramètres par défaut');
      
      // Rafraîchir pour appliquer les changements
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else {
      toast.success('Connexion supprimée');
    }
  };

  // Fonctions d'export/import
  const handleExportConnections = () => {
    try {
      // Préparer les données pour l'export
      const exportObject = {
        connections: connections,
        timestamp: new Date().toISOString()
      };
      
      // Convertir en JSON et chiffrer
      const jsonData = JSON.stringify(exportObject);
      const encryptedData = CryptoJS.AES.encrypt(jsonData, ENCRYPTION_KEY).toString();
      
      setExportData(encryptedData);
      setShowExportModal(true);
    } catch (error) {
      console.error('Erreur lors de l\'export des connexions:', error);
      toast.error('Erreur lors de l\'export des connexions');
    }
  };

  const copyExportData = () => {
    navigator.clipboard.writeText(exportData).then(() => {
      toast.success('Données d\'export copiées dans le presse-papier');
    });
  };

  const handleImportSubmit = () => {
    try {
      if (!importData.trim()) {
        toast.error('Veuillez saisir des données à importer');
        return;
      }
      
      // Tenter de déchiffrer et parser les données
      const bytes = CryptoJS.AES.decrypt(importData, ENCRYPTION_KEY);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedData) {
        throw new Error('Données invalides ou clé incorrecte');
      }
      
      const importedObject = JSON.parse(decryptedData);
      
      if (!importedObject.connections || !Array.isArray(importedObject.connections)) {
        throw new Error('Format de données invalide');
      }
      
      // Fusionner les connexions importées avec les existantes (en évitant les doublons par ID)
      const existingIds = new Set(connections.map(conn => conn.id));
      const newConnections = importedObject.connections.filter((conn: Connection) => !existingIds.has(conn.id));
      
      const updatedConnections = [...connections, ...newConnections];
      
      // Sauvegarder les connexions mises à jour
      localStorage.setItem('saved_connections', JSON.stringify(updatedConnections));
      
      // Mettre à jour l'état
      setConnections(updatedConnections);
      setShowImportModal(false);
      setImportData('');
      
      toast.success(`${newConnections.length} connexions importées avec succès`);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'import des connexions:', error);
      toast.error(`Erreur lors de l'import: ${error.message || 'Format invalide'}`);
    }
  };
  
  const toggleConnectionSelector = () => {
    setShowConnectionSelector(!showConnectionSelector);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {savedData.connectionName ? (
                <span className="text-green-600">Lotis Mode ({savedData.connectionName})</span>
              ) : (
                "Configuration de la Connexion"
              )}
            </h2>
            {/* Bouton pour changer de connexion sans authentification */}
            <button
              onClick={toggleConnectionSelector}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
            >
              Changer de connexion
            </button>
          </div>
        </div>
      </div>

      {/* Sélecteur de connexion simplifié (accessible sans mot de passe) */}
      {showConnectionSelector && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Sélectionner une connexion</h3>
          {connections.length === 0 ? (
            <p className="text-gray-500">Aucune connexion configurée</p>
          ) : (
            <div className="space-y-3">
              {connections.map((conn) => (
                <div 
                  key={conn.id}
                  className={`border rounded-lg p-3 ${conn.isActive ? 'border-green-500 bg-green-50' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-semibold">
                        {conn.connectionName || 'Sans nom'}
                        {conn.isActive && (
                          <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            Actif
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-500 truncate">{conn.supabaseUrl}</p>
                    </div>
                    {!conn.isActive && (
                      <button
                        onClick={() => handleActivateConnection(conn)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                      >
                        Activer
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={toggleConnectionSelector}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {!isAuthenticated ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-6">
            <Lock className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <h3 className="text-xl font-bold">Accès sécurisé</h3>
              <p className="text-gray-600">Veuillez saisir le mot de passe pour accéder aux paramètres de connexion</p>
            </div>
          </div>
          
          <form onSubmit={handleAuthenticate} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className={`w-full p-2 border rounded-md ${passwordError ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Entrez le mot de passe"
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {passwordError && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Accéder aux paramètres
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-6">
            <Database className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <h3 className="text-xl font-bold">Paramètres Supabase</h3>
              <p className="text-gray-600">Configurez les informations de connexion à votre base de données</p>
            </div>
            <div className="ml-auto">
              <button
                onClick={handleExportConnections}
                className="px-3 py-1 mr-2 bg-green-100 text-green-800 text-sm rounded flex items-center"
                title="Exporter les configurations"
              >
                <Download className="w-4 h-4 mr-1" />
                Exporter
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded flex items-center"
                title="Importer des configurations"
              >
                <Upload className="w-4 h-4 mr-1" />
                Importer
              </button>
            </div>
          </div>

          {savedData.supabaseUrl && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Vous utilisez actuellement une configuration personnalisée
                    {savedData.connectionName && <span> : <strong>{savedData.connectionName}</strong></span>}
                  </p>
                  <p className="text-xs text-blue-600 mt-1 truncate">
                    URL: {savedData.supabaseUrl}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Liste des connexions sauvegardées */}
          {connections.length > 0 && (
            <div className="mb-6 border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Connexions sauvegardées</h3>
              <div className="space-y-4">
                {connections.map((conn) => (
                  <div 
                    key={conn.id}
                    className={`border rounded-lg p-3 ${conn.isActive ? 'border-green-500 bg-green-50' : ''}`}
                  >
                    <div className="flex justify-between">
                      <div>
                        <h4 className="font-semibold">
                          {conn.connectionName || 'Sans nom'}
                          {conn.isActive && (
                            <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              Actif
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-500 truncate">{conn.supabaseUrl}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditConnection(conn)}
                          className="p-1 hover:bg-gray-100 rounded-full text-blue-600"
                          title="Modifier"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                        {!conn.isActive && (
                          <button
                            onClick={() => handleActivateConnection(conn)}
                            className="p-1 hover:bg-gray-100 rounded-full text-green-600"
                            title="Activer"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteConnection(conn.id)}
                          className="p-1 hover:bg-gray-100 rounded-full text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4 mb-6">
            <div>
              <label htmlFor="connectionName" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la connexion
              </label>
              <input
                type="text"
                id="connectionName"
                name="connectionName"
                value={formData.connectionName}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder="Entrez un nom pour cette connexion (e.g. Cloud, Production, Local)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ce nom sera affiché dans l'en-tête de l'application pour identifier la connexion active.
              </p>
            </div>
            
            <div>
              <label htmlFor="supabaseUrl" className="block text-sm font-medium text-gray-700 mb-1">
                URL Supabase
              </label>
              <input
                type="text"
                id="supabaseUrl"
                name="supabaseUrl"
                value={formData.supabaseUrl}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder="https://xxxxxxxxxxxx.supabase.co"
              />
            </div>
            
            <div>
              <label htmlFor="supabaseAnonKey" className="block text-sm font-medium text-gray-700 mb-1">
                Clé anonyme Supabase
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="supabaseAnonKey"
                  name="supabaseAnonKey"
                  value={formData.supabaseAnonKey}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md pr-10"
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Vous pouvez trouver ces informations dans les paramètres de votre projet Supabase.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-6 border-t border-gray-200 pt-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 bg-purple-100 p-2 rounded-md">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">Paramètres WooCommerce</h3>
                <p className="text-sm text-gray-600">Configurez l'API WooCommerce pour l'envoi des paniers</p>
              </div>
            </div>

            <div>
              <label htmlFor="wooUrl" className="block text-sm font-medium text-gray-700 mb-1">
                URL WooCommerce
              </label>
              <input
                type="text"
                id="wooUrl"
                value={wooUrl}
                onChange={(e) => setWooUrl(e.target.value)}
                className="w-full p-2 border rounded-md"
                placeholder="https://votresite.com"
              />
            </div>

            <div>
              <label htmlFor="wooConsumerKey" className="block text-sm font-medium text-gray-700 mb-1">
                Clé consommateur WooCommerce
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="wooConsumerKey"
                  value={wooConsumerKey}
                  onChange={(e) => setWooConsumerKey(e.target.value)}
                  className="w-full p-2 border rounded-md pr-10"
                  placeholder="ck_xxxxxxxxxxxxxxxxxxxx"
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="wooConsumerSecret" className="block text-sm font-medium text-gray-700 mb-1">
                Secret consommateur WooCommerce
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="wooConsumerSecret"
                  value={wooConsumerSecret}
                  onChange={(e) => setWooConsumerSecret(e.target.value)}
                  className="w-full p-2 border rounded-md pr-10"
                  placeholder="cs_xxxxxxxxxxxxxxxxxxxx"
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Les clés API se trouvent dans WooCommerce → Réglages → Avancé → API REST.
              </p>
            </div>
          </div>

          {testStatus !== 'idle' && (
            <div className={`p-4 rounded-md mb-6 ${
              testStatus === 'testing' ? 'bg-blue-50 border border-blue-200' :
              testStatus === 'success' ? 'bg-green-50 border border-green-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {testStatus === 'testing' && (
                    <div className="h-5 w-5 border-t-2 border-blue-500 rounded-full animate-spin"></div>
                  )}
                  {testStatus === 'success' && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                  {testStatus === 'error' && (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${
                    testStatus === 'testing' ? 'text-blue-700' :
                    testStatus === 'success' ? 'text-green-700' :
                    'text-red-700'
                  }`}>
                    {testMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {wooTestStatus !== 'idle' && (
            <div className={`p-4 rounded-md mb-6 ${
              wooTestStatus === 'testing' ? 'bg-purple-50 border border-purple-200' :
              wooTestStatus === 'success' ? 'bg-green-50 border border-green-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {wooTestStatus === 'testing' && (
                    <div className="h-5 w-5 border-t-2 border-purple-500 rounded-full animate-spin"></div>
                  )}
                  {wooTestStatus === 'success' && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                  {wooTestStatus === 'error' && (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${
                    wooTestStatus === 'testing' ? 'text-purple-700' :
                    wooTestStatus === 'success' ? 'text-green-700' :
                    'text-red-700'
                  }`}>
                    {wooTestMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Annuler
            </button>
            <button
              onClick={testConnection}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              disabled={saving || testStatus === 'testing'}
            >
              Tester Supabase
            </button>
            <button
              onClick={testWooConnection}
              className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
              disabled={saving || wooTestStatus === 'testing'}
            >
              Tester WooCommerce
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
              disabled={saving || testStatus === 'testing'}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
          
          {/* SQL Information Section */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Info className="w-5 h-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold">Informations Database</h3>
              </div>
              <button
                onClick={() => setShowSqlInfo(!showSqlInfo)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showSqlInfo ? 'Masquer les détails' : 'Afficher les détails'}
              </button>
            </div>
            
            {showSqlInfo && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">1. Création de la table principale</h4>
                    <button 
                      onClick={() => copySqlToClipboard(createTableSQL)}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copier
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                    <pre>{createTableSQL}</pre>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">2. Configuration de la sécurité</h4>
                    <button 
                      onClick={() => copySqlToClipboard(securitySQL)}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copier
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                    <pre>{securitySQL}</pre>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">3. Table de synchronisation</h4>
                    <button 
                      onClick={() => copySqlToClipboard(syncTableSQL)}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copier
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                    <pre>{syncTableSQL}</pre>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">4. Table WooCommerce Order Logs</h4>
                    <button 
                      onClick={() => copySqlToClipboard(wooOrderLogsSQL)}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" /> Copier
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                    <pre>{wooOrderLogsSQL}</pre>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Instructions :</strong> Si vous configurez une nouvelle base de données Supabase, exécutez ces requêtes SQL dans l'ordre pour créer les tables nécessaires au fonctionnement de l'application. Vous pouvez les copier et les coller dans l'éditeur SQL de Supabase.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Bloc : Backups CSV des paniers (dossier local) */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold">Backups CSV des paniers</h3>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={refreshCsvBackups}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Rafraîchir
                </button>
                {csvBackups.length > 0 && (
                  <button
                    onClick={handleClearCsvBackups}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Tout supprimer
                  </button>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Un backup CSV est enregistré automatiquement à chaque « Valider tout », dans le dossier en ligne <span className="font-mono">/csv/</span>. Accessible depuis n'importe quel appareil.
            </p>

            {csvError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                <span>{csvError}</span>
              </div>
            )}

            {csvLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : csvBackups.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">
                Aucun backup CSV pour le moment
              </div>
            ) : (
              <div className="space-y-2">
                {csvBackups.map((backup) => (
                  <div
                    key={backup.name}
                    className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{backup.name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(backup.date).toLocaleString('fr-FR')} · {backup.size}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                      <button
                        onClick={() => handleViewCsvBackup(backup)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                        title="Voir le contenu"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={backup.url}
                        download={backup.name}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDeleteCsvBackup(backup.name)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modal : aperçu du contenu d'un backup CSV (mode tableau + recherche) */}
          <Modal
            isOpen={!!viewingCsv}
            onClose={() => setViewingCsv(null)}
            title={viewingCsv?.name || 'Backup CSV'}
          >
            <div className="space-y-4">
              {viewingCsvLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              ) : (() => {
                const { headers, rows } = parseCsv(viewingCsvContent);
                const term = csvSearch.trim().toLowerCase();
                const isMatch = (row: string[]) =>
                  term !== '' && String(row[0] ?? '').toLowerCase().includes(term);
                const matchCount = term === '' ? 0 : rows.filter(isMatch).length;
                // Remonter les lignes correspondantes tout en haut du tableau
                const orderedRows = term === ''
                  ? rows
                  : [...rows].sort((a, b) => (isMatch(b) ? 1 : 0) - (isMatch(a) ? 1 : 0));

                if (headers.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-400">Fichier vide ou illisible</div>
                  );
                }

                return (
                  <>
                    {/* Recherche par numéro client */}
                    <div>
                      <input
                        type="text"
                        value={csvSearch}
                        onChange={(e) => setCsvSearch(e.target.value)}
                        placeholder="Rechercher un numéro client..."
                        className="w-full p-2 border rounded-md"
                      />
                      {term !== '' && (
                        <p className="text-xs text-gray-500 mt-1">
                          {matchCount} ligne{matchCount > 1 ? 's' : ''} en surbrillance sur {rows.length}
                        </p>
                      )}
                    </div>

                    {/* Tableau */}
                    <div className="border rounded-md max-h-96 overflow-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            {headers.map((h, i) => (
                              <th key={i} className="text-left font-semibold text-gray-700 px-3 py-2 border-b whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td colSpan={headers.length} className="text-center text-gray-400 py-6">
                                Aucune donnée
                              </td>
                            </tr>
                          ) : (
                            orderedRows.map((row, r) => {
                              const highlighted = isMatch(row);
                              return (
                                <tr
                                  key={r}
                                  className={highlighted ? 'bg-yellow-200' : (r % 2 ? 'bg-gray-50' : 'bg-white')}
                                >
                                  {headers.map((_, c) => (
                                    <td key={c} className="px-3 py-2 border-b border-gray-100 align-top">
                                      {row[c] ?? ''}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}

              <div className="flex justify-end space-x-2">
                {viewingCsv && (
                  <a
                    href={viewingCsv.url}
                    download={viewingCsv.name}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </a>
                )}
                <button
                  onClick={() => setViewingCsv(null)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </Modal>

          {/* Modal pour afficher les données d'export */}
          <Modal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            title="Exporter les configurations"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Copiez le texte ci-dessous et sauvegardez-le dans un endroit sûr. Vous pourrez l'utiliser pour restaurer vos configurations sur un autre appareil.
              </p>
              <div className="border rounded-md p-4 bg-gray-50">
                <textarea
                  value={exportData}
                  readOnly
                  className="w-full h-40 bg-gray-50 border-none resize-none focus:ring-0"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={copyExportData}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier
                </button>
              </div>
            </div>
          </Modal>

          {/* Modal pour importer des données */}
          <Modal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            title="Importer des configurations"
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Collez le texte d'export ci-dessous pour restaurer vos configurations.
              </p>
              <div>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="w-full h-40 border rounded-md resize-none p-2"
                  placeholder="Collez ici le texte d'export..."
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={handleImportSubmit}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Importer
                </button>
              </div>
            </div>
          </Modal>

          {/* Modal de confirmation de suppression */}
          <Modal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            title="Supprimer la connexion"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer cette connexion ? Cette action est irréversible.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDeleteConnection}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
}

// SQL Query Examples
const createTableSQL = `-- Create products table
CREATE TABLE IF NOT EXISTS lotismode_products (
  id bigint GENERATED BY DEFAULT AS IDENTITY PRIMARY KEY,
  client_number text NOT NULL,
  items text NOT NULL,
  total_price numeric(10,2) NOT NULL,
  status text NOT NULL CHECK (status IN ('draft', 'pending', 'completed')),
  created_at timestamptz DEFAULT now(),
  updated_at timestamptz DEFAULT now(),
  id_woo bigint
);

-- Create indexes
CREATE INDEX IF NOT EXISTS idx_lotismode_products_status ON lotismode_products(status);
CREATE INDEX IF NOT EXISTS idx_lotismode_products_client_number ON lotismode_products(client_number);
CREATE INDEX IF NOT EXISTS idx_lotismode_products_created_at ON lotismode_products(created_at);

-- Create updated_at trigger function
CREATE OR REPLACE FUNCTION update_updated_at_column()
RETURNS TRIGGER AS $$
BEGIN
  NEW.updated_at = now();
  RETURN NEW;
END;
$$ LANGUAGE plpgsql;

-- Create trigger for updated_at
CREATE TRIGGER update_lotismode_products_updated_at
  BEFORE UPDATE ON lotismode_products
  FOR EACH ROW
  EXECUTE FUNCTION update_updated_at_column();`;

const securitySQL = `-- Enable RLS
ALTER TABLE lotismode_products ENABLE ROW LEVEL SECURITY;

-- Grant access to authenticated and anonymous users
GRANT ALL ON lotismode_products TO anon, authenticated;
GRANT USAGE ON SEQUENCE lotismode_products_id_seq TO anon, authenticated;

-- Create RLS policies
CREATE POLICY "Enable read access for all users"
  ON lotismode_products FOR SELECT
  TO anon, authenticated
  USING (true);

CREATE POLICY "Enable insert access for all users"
  ON lotismode_products FOR INSERT
  TO anon, authenticated
  WITH CHECK (true);

CREATE POLICY "Enable update access for all users"
  ON lotismode_products FOR UPDATE
  TO anon, authenticated
  USING (true)
  WITH CHECK (true);

CREATE POLICY "Enable delete access for all users"
  ON lotismode_products FOR DELETE
  TO anon, authenticated
  USING (true);`;

const syncTableSQL = `-- Create sync_sessions table
CREATE TABLE IF NOT EXISTS sync_sessions (
  id uuid PRIMARY KEY,
  started_at timestamptz NOT NULL DEFAULT now(),
  completed_at timestamptz,
  status text NOT NULL CHECK (status IN ('in_progress', 'completed', 'completed_with_errors', 'failed')),
  total_products integer NOT NULL DEFAULT 0,
  synced_products integer NOT NULL DEFAULT 0,
  failed_products integer NOT NULL DEFAULT 0,
  updated_at timestamptz NOT NULL DEFAULT now()
);

-- Add sync tracking columns to products table
ALTER TABLE lotismode_products ADD COLUMN IF NOT EXISTS sync_status text CHECK (sync_status IN ('pending', 'synced', 'failed', NULL));
ALTER TABLE lotismode_products ADD COLUMN IF NOT EXISTS sync_id uuid;
ALTER TABLE lotismode_products ADD COLUMN IF NOT EXISTS sync_error text;

-- Create indexes
CREATE INDEX IF NOT EXISTS idx_sync_sessions_status ON sync_sessions(status);
CREATE INDEX IF NOT EXISTS idx_sync_sessions_started_at ON sync_sessions(started_at);
CREATE INDEX IF NOT EXISTS idx_lotismode_products_sync_id ON lotismode_products(sync_id);
CREATE INDEX IF NOT EXISTS idx_lotismode_products_sync_status ON lotismode_products(sync_status);

-- Enable RLS
ALTER TABLE sync_sessions ENABLE ROW LEVEL SECURITY;

-- Grant access to authenticated and anonymous users
GRANT ALL ON sync_sessions TO anon, authenticated;

-- Create RLS policies
CREATE POLICY "Enable read access for all users"
  ON sync_sessions FOR SELECT
  TO anon, authenticated
  USING (true);

CREATE POLICY "Enable insert access for all users"
  ON sync_sessions FOR INSERT
  TO anon, authenticated
  WITH CHECK (true);

CREATE POLICY "Enable update access for all users"
  ON sync_sessions FOR UPDATE
  TO anon, authenticated
  USING (true)
  WITH CHECK (true);

CREATE POLICY "Enable delete access for all users"
  ON sync_sessions FOR DELETE
  TO anon, authenticated
  USING (true);`;

const wooOrderLogsSQL = `-- Table pour stocker les logs de commandes WooCommerce
CREATE TABLE IF NOT EXISTS woo_order_logs (
  id BIGSERIAL PRIMARY KEY,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  client_number TEXT NOT NULL,
  product_id BIGINT,
  woo_product_id BIGINT,
  order_id BIGINT,
  order_key TEXT,
  payment_url TEXT,
  customer_email TEXT,
  customer_name TEXT,
  customer_id BIGINT,
  status TEXT NOT NULL DEFAULT 'success',
  error_message TEXT,
  order_total TEXT,
  order_status TEXT DEFAULT 'pending'
);

-- Index pour les recherches fréquentes
CREATE INDEX IF NOT EXISTS idx_woo_order_logs_order_id ON woo_order_logs(order_id);
CREATE INDEX IF NOT EXISTS idx_woo_order_logs_client_number ON woo_order_logs(client_number);
CREATE INDEX IF NOT EXISTS idx_woo_order_logs_order_status ON woo_order_logs(order_status);
CREATE INDEX IF NOT EXISTS idx_woo_order_logs_created_at ON woo_order_logs(created_at DESC);

-- Activer RLS
ALTER TABLE woo_order_logs ENABLE ROW LEVEL SECURITY;

-- Politique pour permettre toutes les opérations
CREATE POLICY "Allow all operations on woo_order_logs" ON woo_order_logs
  FOR ALL
  USING (true)
  WITH CHECK (true);

-- Accès pour les utilisateurs anon et authenticated
GRANT ALL ON woo_order_logs TO anon, authenticated;
GRANT USAGE ON SEQUENCE woo_order_logs_id_seq TO anon, authenticated;`;